// Diário › Conquistas (0.65): Mural (metas, sonhos e hábitos concluídos, com a data), Relatório de realizações
// (semana, mês, ano ou personalizado, com PDF) e Revisão do mês (humor, metas que avançaram, hábitos e reflexão).
import { useState } from 'react';
import { Ditado } from '../Ditado';
import { Alert, Pressable, Text, TextInput, View } from 'react-native';
import { useAllDocs } from '../../store/store';
import { confDiario, diasEscritos, gravar, ler, lerDia, lerHabitos, lerMetas, mudarConf, useDiario } from '../../store/diario';
import type { Docs } from '../../store/types';
import { Chip, Screen } from '../ui';
import { C, alpha } from '../theme';
import { PickerModal } from '../Pickers';
import { RelatoriosGerados } from '../RelatoriosGerados';
import { Barra, HUMORES, Recolhe, dataLonga, emojiHumor, tarefaDe } from './comum';
import { TIPOS } from './ProjetoVida';
import { noPeriodo, progressoMeta } from '../../lib/habitos';
import { gerarECompartilharPdf, type BlocoPdf } from '../../lib/pdf';
import { addDays, addMonths, nomeMes, parseYmd, today, weekStart, ymd } from '../../lib/date';
import { t } from '../../lib/i18n';

type Periodo = 'semana' | 'mes' | 'ano' | 'pers';

function humorDoPeriodo(de: string, ate: string) {
  const ns = diasEscritos().filter(d => d >= de && d <= ate).map(d => lerDia(d).humor).filter((x): x is number => !!x);
  const media = ns.length ? ns.reduce((s, x) => s + x, 0) / ns.length : null;
  const cont = HUMORES.map(([n, e]) => [e, ns.filter(x => x === n).length] as const).filter(([, c]) => c);
  return { media, cont, n: ns.length };
}

function dadosPeriodo(de: string, ate: string, docs: Docs) {
  const metas = lerMetas(), habitos = lerHabitos();
  return {
    concluidas: metas.filter(m => m.concluida && m.concluida >= de && m.concluida <= ate),
    formados: habitos.filter(h => h.formado && h.formado >= de && h.formado <= ate),
    habitos: habitos.filter(h => !h.formado || h.formado >= de).map(h => ({ h, ...noPeriodo(h, de, ate, docs, tarefaDe('hab:' + h.id)) })),
    andamento: metas.filter(m => !m.concluida).map(m => ({ m, p: progressoMeta(m, habitos, docs, id => tarefaDe('hab:' + id)) })),
    humor: humorDoPeriodo(de, ate),
  };
}

export function Conquistas({ cor, onBack }: { cor: string; onBack: () => void }) {
  useDiario();
  const docs = useAllDocs();
  const [aba, setAba] = useState<'mural' | 'rel' | 'rev'>('mural');
  return (
    <Screen title={t('Conquistas')} onBack={onBack}>
      <View style={{ flexDirection: 'row', gap: 6 }}>
        <Chip cor={cor} on={aba === 'mural'} label={'🏆 ' + t('Mural')} onPress={() => setAba('mural')} />
        <Chip cor={cor} on={aba === 'rel'} label={'📊 ' + t('Relatório')} onPress={() => setAba('rel')} />
        <Chip cor={cor} on={aba === 'rev'} label={'🗓️ ' + t('Revisão do mês')} onPress={() => setAba('rev')} />
      </View>
      {aba === 'mural' && <Mural cor={cor} />}
      {aba === 'rel' && <Relatorio cor={cor} docs={docs} />}
      {aba === 'rev' && <Revisao cor={cor} docs={docs} />}
      <View style={{ height: 60 }} />
    </Screen>
  );
}

function Mural({ cor }: { cor: string }) {
  const metas = lerMetas().filter(m => m.concluida).map(m => ({ id: m.id, icone: m.icone, titulo: m.titulo, data: m.concluida!, rot: t(TIPOS.find(x => x[0] === m.tipo)?.[1] ?? '') + ' ' + t('concluído') }));
  const habs = lerHabitos().filter(h => h.formado).map(h => ({ id: h.id, icone: h.icone, titulo: h.nome, data: h.formado!, rot: t('hábito formado ✨') }));
  const l = [...metas, ...habs].sort((a, b) => b.data.localeCompare(a.data));
  if (!l.length) return <Text style={{ color: C.sub }}>{t('Quando você concluir uma meta ou formar um hábito, ele aparece aqui para celebrar. 🎉')}</Text>;
  const cores = ['#F59E0B', '#EC4899', '#8B5CF6', '#10B981', '#3B82F6', '#EF4444'];
  return (
    <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10 }}>
      {l.map((x, i) => (
        <View key={x.id} style={{ width: '47%', backgroundColor: alpha(cores[i % cores.length], 0.16), borderRadius: 18, padding: 14, gap: 6, borderWidth: 2, borderColor: alpha(cores[i % cores.length], 0.6), alignItems: 'center' }}>
          <Text style={{ fontSize: 12 }}>🎉 ✨ 🎊</Text>
          <Text style={{ fontSize: 34 }}>{x.icone}</Text>
          <Text style={{ color: C.text, fontWeight: '800', textAlign: 'center' }} numberOfLines={3}>{x.titulo}</Text>
          <Text style={{ color: cores[i % cores.length], fontSize: 11, fontWeight: '800', textAlign: 'center' }}>{x.rot}</Text>
          <Text style={{ color: C.sub, fontSize: 11 }}>{dataLonga(x.data)}</Text>
        </View>
      ))}
    </View>
  );
}

function intervalo(p: Periodo, ref: string, de: string, ate: string): [string, string] {
  if (p === 'semana') { const s = weekStart(ref); return [s, addDays(s, 6)]; }
  if (p === 'mes') { const d = parseYmd(ref); return [ymd(new Date(d.getFullYear(), d.getMonth(), 1)), ymd(new Date(d.getFullYear(), d.getMonth() + 1, 0))]; }
  if (p === 'ano') return [ref.slice(0, 4) + '-01-01', ref.slice(0, 4) + '-12-31'];
  return [de, ate];
}

function Relatorio({ cor, docs }: { cor: string; docs: Docs }) {
  const [p, setP] = useState<Periodo>('mes');
  const [ref, setRef] = useState(today());
  const [de, setDe] = useState(addDays(today(), -29));
  const [ate, setAte] = useState(today());
  const [pick, setPick] = useState<'de' | 'ate' | null>(null);
  const [ini, fim] = intervalo(p, ref, de, ate);
  const d = dadosPeriodo(ini, fim, docs);
  const mover = (n: number) => setRef(r => (p === 'semana' ? addDays(r, 7 * n) : p === 'mes' ? ymd(addMonths(parseYmd(r), n)) : `${Number(r.slice(0, 4)) + n}${r.slice(4)}`));
  const exportar = () => {
    const b: BlocoPdf[] = [{ t: 'titulo', texto: t('Relatório de realizações') }, { t: 'sub', texto: `${dataLonga(ini)} – ${dataLonga(fim)}` }, { t: 'linha' }];
    b.push({ t: 'par', texto: t('Metas e sonhos concluídos'), negrito: true });
    b.push(...(d.concluidas.length ? d.concluidas.map(m => ({ t: 'par' as const, texto: `• ${m.titulo} — ${dataLonga(m.concluida!)}` })) : [{ t: 'par' as const, texto: t('Nenhum no período.') }]));
    b.push({ t: 'par', texto: t('Hábitos'), negrito: true });
    b.push({ t: 'tabela', colunas: [t('Hábito'), t('Esperado'), t('Feito'), t('Faltou')], linhas: d.habitos.map(x => [x.h.nome, String(x.plan), String(x.feitos), String(Math.max(0, x.plan - x.feitos))]) });
    b.push({ t: 'par', texto: t('Metas em andamento'), negrito: true });
    b.push(...d.andamento.map(x => ({ t: 'par' as const, texto: `• ${x.m.titulo}: ${Math.round(x.p * 100)}%` })));
    b.push({ t: 'par', texto: t('Humor do período'), negrito: true });
    b.push({ t: 'par', texto: d.humor.n ? t('{n} dia(s) registrado(s), média {m} de 6', { n: d.humor.n, m: d.humor.media!.toFixed(1) }) : t('Sem registros de humor.') });
    gerarECompartilharPdf(t('Relatório de realizações') + ` ${ini} a ${fim}`, b).catch(e => Alert.alert(t('Não foi possível'), String(e?.message ?? e)));
  };
  return (
    <View style={{ gap: 10 }}>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
        {([['semana', t('Semana')], ['mes', t('Mês')], ['ano', t('Ano')], ['pers', t('Personalizado')]] as [Periodo, string][]).map(([k, n]) => <Chip key={k} small cor={cor} on={p === k} label={n} onPress={() => setP(k)} />)}
      </View>
      {p === 'pers' ? (
        <View style={{ flexDirection: 'row', gap: 8 }}>
          <Chip cor={cor} on={false} label={t('De {d}', { d: dataLonga(de) })} onPress={() => setPick('de')} />
          <Chip cor={cor} on={false} label={t('Até {d}', { d: dataLonga(ate) })} onPress={() => setPick('ate')} />
        </View>
      ) : (
        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
          <Pressable onPress={() => mover(-1)} hitSlop={10} style={{ padding: 6 }}><Text style={{ color: C.text, fontSize: 20 }}>‹</Text></Pressable>
          <Text style={{ flex: 1, textAlign: 'center', color: C.text, fontWeight: '700' }}>{dataLonga(ini)} – {dataLonga(fim)}</Text>
          <Pressable onPress={() => mover(1)} hitSlop={10} style={{ padding: 6 }}><Text style={{ color: C.text, fontSize: 20 }}>›</Text></Pressable>
        </View>
      )}
      <Recolhe titulo={`🏆 ${t('Metas e sonhos concluídos')} (${d.concluidas.length})`}>
        {d.concluidas.map(m => <Text key={m.id} style={{ color: C.text }}>{m.icone} {m.titulo} · <Text style={{ color: C.sub }}>{dataLonga(m.concluida!)}</Text></Text>)}
        {!d.concluidas.length && <Text style={{ color: C.sub }}>{t('Nenhum no período.')}</Text>}
      </Recolhe>
      <Recolhe titulo={`🔥 ${t('Hábitos')} (${d.habitos.length})`}>
        {d.habitos.map(x => (
          <View key={x.h.id} style={{ gap: 3 }}>
            <Text style={{ color: C.text }}>{x.h.icone} {x.h.nome}</Text>
            <Text style={{ color: C.sub, fontSize: 12 }}>{t('Esperado: {e} · Feito: {f} · Faltou: {fa}', { e: x.plan, f: x.feitos, fa: Math.max(0, x.plan - x.feitos) })} · {x.plan ? Math.round((x.feitos / x.plan) * 100) : 0}%</Text>
            <Barra p={x.plan ? x.feitos / x.plan : 0} cor={cor} alt={6} />
          </View>
        ))}
      </Recolhe>
      <Recolhe titulo={`🎯 ${t('Metas em andamento')} (${d.andamento.length})`}>
        {d.andamento.map(x => (
          <View key={x.m.id} style={{ gap: 3 }}>
            <Text style={{ color: C.text }}>{x.m.icone} {x.m.titulo} · {Math.round(x.p * 100)}%</Text>
            <Barra p={x.p} cor={cor} alt={6} />
          </View>
        ))}
      </Recolhe>
      <Recolhe titulo={`😊 ${t('Humor do período')}`}>
        <Text style={{ color: C.text }}>{d.humor.n ? t('{n} dia(s) registrado(s) · média {e}', { n: d.humor.n, e: emojiHumor(Math.round(d.humor.media!)) }) : t('Sem registros de humor.')}</Text>
        <Text style={{ color: C.sub, fontSize: 16 }}>{d.humor.cont.map(([e, c]) => `${e} ${c}`).join('   ')}</Text>
      </Recolhe>
      <Pressable onPress={exportar} style={{ backgroundColor: cor, borderRadius: 14, padding: 14, alignItems: 'center' }}><Text style={{ color: '#fff', fontWeight: '800' }}>📕 {t('Exportar em PDF')}</Text></Pressable>
      <RelatoriosGerados aba="diario" cor={cor} />
      {pick && <PickerModal mode="date" cor={cor} value={parseYmd(pick === 'de' ? de : ate)} onClose={() => setPick(null)} onPick={x => (pick === 'de' ? setDe(ymd(x)) : setAte(ymd(x)))} />}
    </View>
  );
}

function Revisao({ cor, docs }: { cor: string; docs: Docs }) {
  const [ref, setRef] = useState(today().slice(0, 7));
  const ini = ref + '-01', fim = ymd(new Date(Number(ref.slice(0, 4)), Number(ref.slice(5, 7)), 0));
  const d = dadosPeriodo(ini, fim, docs);
  const [txt, setTxt] = useState(() => ler<string>('revisao:' + ref, ''));
  const mudarMes = (n: number) => { const x = addMonths(parseYmd(ref + '-01'), n); const k = ymd(x).slice(0, 7); setRef(k); setTxt(ler<string>('revisao:' + k, '')); };
  const conf = confDiario();
  const avancaram = d.andamento.filter(x => x.p > 0);
  return (
    <View style={{ gap: 10 }}>
      <View style={{ flexDirection: 'row', alignItems: 'center' }}>
        <Pressable onPress={() => mudarMes(-1)} hitSlop={10} style={{ padding: 6 }}><Text style={{ color: C.text, fontSize: 20 }}>‹</Text></Pressable>
        <Text style={{ flex: 1, textAlign: 'center', color: C.text, fontWeight: '800', fontSize: 16 }}>{nomeMes(Number(ref.slice(5, 7)) - 1)} {ref.slice(0, 4)}</Text>
        <Pressable onPress={() => mudarMes(1)} disabled={ref >= today().slice(0, 7)} hitSlop={10} style={{ padding: 6, opacity: ref >= today().slice(0, 7) ? 0.3 : 1 }}><Text style={{ color: C.text, fontSize: 20 }}>›</Text></Pressable>
      </View>
      <View style={{ backgroundColor: C.card, borderRadius: 16, padding: 12, gap: 8 }}>
        <Text style={{ color: C.text }}>😊 {t('Humor do mês')}: {d.humor.n ? `${emojiHumor(Math.round(d.humor.media!))} (${d.humor.cont.map(([e, c]) => `${e}${c}`).join(' ')})` : t('sem registros')}</Text>
        <Text style={{ color: C.text }}>🎯 {t('Metas que avançaram')}: {avancaram.length}</Text>
        {avancaram.map(x => <Text key={x.m.id} style={{ color: C.sub, fontSize: 13 }}>   {x.m.icone} {x.m.titulo} · {Math.round(x.p * 100)}%</Text>)}
        <Text style={{ color: C.text }}>🔥 {t('Hábitos cumpridos')}: {d.habitos.reduce((s, x) => s + x.feitos, 0)} {t('de {n} planejados', { n: d.habitos.reduce((s, x) => s + x.plan, 0) })}</Text>
        <Text style={{ color: C.text }}>🏆 {t('Conquistas')}: {d.concluidas.length + d.formados.length}</Text>
        {[...d.concluidas.map(m => `${m.icone} ${m.titulo}`), ...d.formados.map(h => `${h.icone} ${h.nome} ✨`)].map(x => <Text key={x} style={{ color: C.sub, fontSize: 13 }}>   {x}</Text>)}
      </View>
      <Text style={{ color: C.sub, fontSize: 12, fontWeight: '800' }}>{t('REFLEXÃO DO MÊS')}</Text>
      <View>
        <TextInput multiline value={txt} onChangeText={v => { setTxt(v); gravar('revisao:' + ref, v); }} placeholder={t('O que este mês te ensinou? O que quer levar para o próximo?')} placeholderTextColor={C.mute}
          style={{ backgroundColor: C.card, borderRadius: 14, padding: 12, paddingRight: 44, color: C.text, minHeight: 120, textAlignVertical: 'top', fontSize: 15 }} />
        <View style={{ position: 'absolute', right: 6, bottom: 6 }}><Ditado pequeno cor={cor} onTexto={tx => { const v = txt ? txt + ' ' + tx : tx; setTxt(v); gravar('revisao:' + ref, v); }} /></View>
      </View>
      <Pressable onPress={() => mudarConf({ revisao: !conf.revisao })} style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
        <Text style={{ fontSize: 20, color: C.text }}>{conf.revisao ? '☑' : '☐'}</Text>
        <Text style={{ color: C.text, flex: 1 }}>{t('Aviso no último dia do mês: "Que tal revisar o seu mês? ✨"')}</Text>
      </Pressable>
    </View>
  );
}
